import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Trash2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = { kind: "officer", name: "", role: "", phone: "", photo_url: "" };
const inputCls =
  "w-full bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 text-clay-text placeholder:text-clay-muted/60 transition-colors duration-300";

export default function AdminCommittee({ token }) {
  const [officers, setOfficers] = useState([]);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/committee");
      setOfficers(data.officers || []);
      setMembers(data.members || []);
    } catch {
      toast.error("Failed to load committee");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (photo) fd.append("photo", photo);
      if (editId) {
        await api.put(`/admin/committee/${editId}`, fd, { headers, timeout: 120000 });
        toast.success("Committee member updated");
      } else {
        await api.post("/admin/committee", fd, { headers, timeout: 120000 });
        toast.success(`${form.name} added to the committee`);
      }
      setForm(EMPTY);
      setPhoto(null);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const edit = (m) => {
    setForm({ kind: m.kind || "officer", name: m.name || "", role: m.role || "", phone: m.phone || "", photo_url: m.img?.startsWith("/api") ? "" : m.img || "" });
    setPhoto(null);
    setEditId(m.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the committee?`)) return;
    try {
      await api.delete(`/admin/committee/${m.id}`, { headers });
      toast.success(`${m.name} removed`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const imgSrc = (u) => (u?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10 space-y-12">
      <section>
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-5">
          {editId ? "Edit Committee Member" : "Add Committee Member"}
        </h2>
        <form data-testid="committee-form" onSubmit={save} className="border border-clay-border bg-clay-card p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <select data-testid="committee-kind-select" value={form.kind} onChange={set("kind")} className={inputCls}>
            <option value="officer">Officer (photo, role, phone)</option>
            <option value="member">Dedicated Member (name only)</option>
          </select>
          <input data-testid="committee-name-input" placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} required />
          {form.kind === "officer" && (
            <>
              <input data-testid="committee-role-input" placeholder="Role — e.g. President, Secretary" value={form.role} onChange={set("role")} className={inputCls} />
              <input data-testid="committee-phone-input" placeholder="Phone — e.g. 0427 565 956" value={form.phone} onChange={set("phone")} className={inputCls} />
              <div className="sm:col-span-2">
                <input
                  data-testid="committee-photo-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                  className="w-full text-sm text-clay-muted file:mr-4 file:py-3 file:px-6 file:border-0 file:bg-clay-elevated file:text-clay-text file:font-display file:uppercase file:tracking-wider hover:file:bg-clay-orange hover:file:text-clay-bg file:transition-colors file:cursor-pointer"
                />
              </div>
              <input
                data-testid="committee-photourl-input"
                placeholder="…or paste a photo URL (optional)"
                value={form.photo_url}
                onChange={set("photo_url")}
                className={`${inputCls} sm:col-span-2`}
              />
            </>
          )}
          <div className="sm:col-span-2 flex gap-3">
            {editId && (
              <button
                data-testid="committee-cancel-button"
                type="button"
                onClick={() => { setEditId(null); setForm(EMPTY); setPhoto(null); }}
                className="flex-1 border border-clay-border hover:border-clay-orange font-display font-bold uppercase tracking-wider px-6 py-4 transition-colors flex items-center justify-center gap-2"
              >
                <X className="w-5 h-5" /> Cancel
              </button>
            )}
            <button
              data-testid="committee-save-button"
              disabled={saving}
              className="flex-[2] bg-clay-orange hover:bg-clay-gold disabled:opacity-60 text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-6 py-4 transition-colors flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserPlus className="w-5 h-5" />}
              {editId ? "Save Changes" : "Add to Committee"}
            </button>
          </div>
        </form>
      </section>

      <section>
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-5">Officers ({officers.length})</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {officers.map((m) => (
            <div key={m.id} data-testid="committee-admin-officer" className="border border-clay-border bg-clay-card">
              {m.img ? (
                <img src={imgSrc(m.img)} alt={m.name} className="w-full aspect-square object-cover" loading="lazy" />
              ) : (
                <div className="w-full aspect-square bg-clay-elevated grid place-items-center font-display font-black text-4xl text-clay-muted">
                  {m.name?.[0]}
                </div>
              )}
              <div className="p-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-orange">{m.role}</p>
                <p className="font-display font-bold uppercase text-lg tracking-tight">{m.name}</p>
                <p className="font-mono text-xs text-clay-muted">{m.phone}</p>
                <div className="flex gap-2 mt-3">
                  <button data-testid="committee-edit-button" onClick={() => edit(m)} className="text-clay-muted hover:text-clay-gold transition-colors p-1" aria-label="Edit">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid="committee-delete-button" onClick={() => remove(m)} className="text-clay-muted hover:text-red-400 transition-colors p-1" aria-label="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-5">Dedicated Members ({members.length})</h2>
        <div className="flex flex-wrap gap-3">
          {members.map((m) => (
            <span key={m.id} data-testid="committee-admin-member" className="flex items-center gap-3 border border-clay-border bg-clay-card px-4 py-2.5">
              <span className="font-display font-bold uppercase tracking-wide">{m.name}</span>
              <button data-testid="committee-member-edit" onClick={() => edit(m)} className="text-clay-muted hover:text-clay-gold transition-colors" aria-label="Edit">
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button data-testid="committee-member-delete" onClick={() => remove(m)} className="text-clay-muted hover:text-red-400 transition-colors" aria-label="Delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
